import type {
  CloudpackConfig,
  PackageDefinitionsCache,
  GeneratedPackageSettings,
  GeneratedExports,
} from '@ms-cloudpack/common-types';
import { isExternalPackage } from '@ms-cloudpack/path-utilities';
import { getGeneratedPackageSettings } from './getGeneratedPackageSettings.js';

/**
 * Ensures the generated package setting for the package provided exists.
 * Used to consistently write to the generated config.
 * Only call this function when you will create a package setting for the package.
 * Note: Does not write to disk.
 */
export async function ensureGeneratedSettingsForPackage(
  options: { packagePath: string; defaultExportsMap?: GeneratedExports },
  context: { config: Pick<CloudpackConfig, 'generated'>; packages: PackageDefinitionsCache },
): Promise<GeneratedPackageSettings> {
  const { packagePath } = options;
  const { config, packages } = context;

  const definition = await packages.get(packagePath);

  const { name, version } = definition;
  let generatedPackageSettings = getGeneratedPackageSettings({
    generatedPackageSettings: config.generated.packageSettings,
    name,
    version,
  });

  if (!generatedPackageSettings) {
    generatedPackageSettings = {
      match: isExternalPackage(packagePath) ? { name, version: `^${version}` } : name,
      ...(options.defaultExportsMap && { exports: options.defaultExportsMap }),
    };

    config.generated.packageSettings ??= [];
    config.generated.packageSettings.push(generatedPackageSettings);
  }

  return generatedPackageSettings;
}
